import { NftRedemptionModel } from '../models';
import { useState } from 'react';
import './NftRedemptionsTable.css';

interface NftRedemptionsTableProps {
   nftRedemptions: NftRedemptionModel[];
}

export const NftRedemptionsTable = ({ nftRedemptions }: NftRedemptionsTableProps) => {
   const [searchTerm, setSearchTerm] = useState('');
   const [copiedKey, setCopiedKey] = useState<string | null>(null);

   const getTensorUrl = (publicKey: string) => `https://tensor.trade/item/${publicKey}`;
   const getMagicEdenUrl = (publicKey: string) => `https://magiceden.io/item-details/${publicKey}`;

   const formatDate = (date: string | null) => {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', {
         year: 'numeric',
         month: 'short',
         day: 'numeric',
         hour: '2-digit',
         minute: '2-digit',
      });
   };

   const getRarityClass = (rarity: string) => {
      switch (rarity.toLowerCase()) {
         case 'legendary':
         case 'one-of-one':
            return 'rarity-legendary';
         case 'epic':
            return 'rarity-epic';
         case 'rare':
            return 'rarity-rare';
         case 'uncommon':
            return 'rarity-uncommon';
         case 'common':
            return 'rarity-common';
         default:
            return 'rarity-common';
      }
   };

   const copyToClipboard = async (text: string) => {
      try {
         await navigator.clipboard.writeText(text);
         setCopiedKey(text);
         setTimeout(() => setCopiedKey(null), 2000); // Reset after 2 seconds
      } catch (err) {
         console.error('Failed to copy text: ', err);
      }
   };

   const filteredRedemptions = nftRedemptions.filter(
      (redemption) =>
         redemption.nft.clubType.toLowerCase().includes(searchTerm.toLowerCase()) ||
         redemption.nft.publicKey.toLowerCase().includes(searchTerm.toLowerCase()) ||
         redemption.nft.rarity.toLowerCase().includes(searchTerm.toLowerCase())
   );

   if (nftRedemptions.length === 0) {
      return (
         <div className="empty-state">
            <p>No GolfN G-Covers found.</p>
         </div>
      );
   }

   return (
      <div className="nft-redemptions-container">
         <div className="search-container">
            <input
               type="text"
               placeholder="Search NFTs by name, address, or rarity..."
               value={searchTerm}
               onChange={(e) => setSearchTerm(e.target.value)}
               className="search-input"
            />
         </div>
         <div className="nft-redemptions-table">
            <table>
               <thead>
                  <tr>
                     <th>NFT</th>
                     <th>Rarity</th>
                     <th>Status</th>
                     <th></th>
                  </tr>
               </thead>
               <tbody>
                  {filteredRedemptions.map((redemption, index) => (
                     <tr key={index}>
                        <td>
                           <div style={{ display: 'flex', alignItems: 'center' }}>
                              <div style={{ flexShrink: 0 }}>
                                 <img
                                    className="nft-image"
                                    src={redemption.nft.imageUrl}
                                    alt={`NFT ${redemption.nft.publicKey}`}
                                    onError={(e) => {
                                       const target = e.target as HTMLImageElement;
                                       target.src = '/assets/images/placeholder-nft.png';
                                    }}
                                 />
                              </div>
                              <div className="nft-info">
                                 <div className="nft-name">{redemption.nft.clubType}</div>
                                 <div className="nft-public-key-container relative">
                                    <div
                                       className="nft-public-key clickable"
                                       onClick={() => copyToClipboard(redemption.nft.publicKey)}
                                       title="Click to copy full public key">
                                       {redemption.nft.publicKey.slice(0, 15)}...
                                    </div>
                                    {copiedKey === redemption.nft.publicKey && <span className="copied-message">copied!</span>}
                                 </div>
                              </div>
                           </div>
                        </td>
                        <td>
                           <span
                              className={`${getRarityClass(redemption.nft.rarity)}`}
                              style={{ textTransform: 'capitalize' }}>
                              {redemption.nft.rarity}
                           </span>
                        </td>
                        <td>
                           <div className="status-container">
                              <span className={`status-badge ${redemption.isRedeemed ? 'status-redeemed' : 'status-available'}`}>
                                 {redemption.isRedeemed ? 'Redeemed' : 'Available'}
                              </span>
                              {redemption.isRedeemed && redemption.redeemedAt ? (
                                 <span className="status-redeemed-date">
                                    {new Date(redemption.redeemedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                 </span>
                              ) : (
                                 ''
                              )}
                           </div>
                        </td>
                        <td>
                           <div className="action-links">
                              <a
                                 href={getTensorUrl(redemption.nft.publicKey)}
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 className="action-link">
                                 Tensor
                              </a>
                              <span className="action-separator">|</span>
                              <a
                                 href={getMagicEdenUrl(redemption.nft.publicKey)}
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 className="action-link">
                                 Magic Eden
                              </a>
                           </div>
                        </td>
                     </tr>
                  ))}
               </tbody>
            </table>
         </div>
      </div>
   );
};
